import { useEffect, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { useServices } from '@/context/ServiceContext'
import { useAuth } from '@/context/AuthContext'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { AskSarahButton } from '@/components/sarah/AskSarahButton'
import { getResultNarrative } from './quizNarrative'
import type { Episode } from '@/domain/episode'
import type { Question } from '@/domain/question'
import type { QuizResult } from '@/domain/result'
import type { BadgeDefinition } from '@/domain/badge'

interface LocationState {
  freshlyAwardedBadgeKeys?: string[]
  xpEarnedThisAttempt?: number
  freshCertificateId?: string | null
}

export function QuizResultsPage() {
  const { id, resultId } = useParams<{ id: string; resultId: string }>()
  const location = useLocation()
  const state = (location.state as LocationState | undefined) ?? {}
  const { episodes, quiz, results, badges } = useServices()
  const { currentUser } = useAuth()

  const [episode, setEpisode] = useState<Episode | null>(null)
  const [questions, setQuestions] = useState<Question[]>([])
  const [result, setResult] = useState<QuizResult | null>(null)
  const [badgeDefs, setBadgeDefs] = useState<BadgeDefinition[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!id || !resultId) return
    Promise.all([
      episodes.getById(id),
      quiz.getQuestionsForEpisode(id),
      results.getById(resultId),
      badges.listDefinitions(),
    ]).then(([ep, qs, res, defs]) => {
      setEpisode(ep)
      setQuestions(qs)
      setResult(res)
      setBadgeDefs(defs)
      setLoading(false)
    })
  }, [id, resultId, episodes, quiz, results, badges])

  if (loading) return <LoadingSpinner label="Scoring your quiz…" />

  if (!episode || !result || !currentUser) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold text-white">Result not found</h1>
        <p className="mt-3 text-white/60">
          This result may have been cleared, or you're viewing it from a different browser/session.
        </p>
        <Link to="/episodes" className="cyber-btn-primary mt-8 inline-flex">
          Back to Episode Library
        </Link>
      </div>
    )
  }

  const narrative = getResultNarrative(episode.id, result.passed)
  const questionById = new Map(questions.map((q) => [q.id, q]))
  const freshBadges = badgeDefs.filter((d) => state.freshlyAwardedBadgeKeys?.includes(d.badgeKey))

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
      <Link to={`/episodes/${episode.id}`} className="text-sm text-white/50 hover:text-white">
        ← {episode.title}
      </Link>

      <div className={`cyber-panel mt-4 p-8 text-center ${result.passed ? 'border-teal-400/30' : 'border-coral-500/20'}`}>
        <p className="text-5xl">{result.passed ? '🎉' : '🔁'}</p>
        <p className={`mt-4 font-display text-2xl font-bold ${result.passed ? 'gradient-text' : 'text-white'}`}>
          {narrative}
        </p>
        <p className="mt-4 text-white/70">
          You scored <span className="font-semibold text-white">{result.scorePercent}%</span>
          {' · '}
          {result.answers.filter((a) => a.isCorrect).length} of {result.answers.length} correct
          {result.attemptNumber > 1 && <> · Attempt {result.attemptNumber}</>}
        </p>

        {result.passed && (state.xpEarnedThisAttempt ?? 0) > 0 && (
          <p className="mt-3 cyber-pill mx-auto w-fit text-amber-300">
            <span className="cyber-pill-dot" style={{ background: '#F2A93B', boxShadow: '0 0 6px 1px rgba(242,169,59,0.7)' }} />
            +{state.xpEarnedThisAttempt} Cyber Points earned
          </p>
        )}
      </div>

      {freshBadges.length > 0 && (
        <section className="mt-6">
          <h2 className="font-display text-lg font-semibold text-white">New badge{freshBadges.length > 1 ? 's' : ''} earned!</h2>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            {freshBadges.map((b) => (
              <div key={b.badgeKey} className="cyber-panel flex items-center gap-3 border-amber-400/30 p-4">
                <span className="text-3xl">{b.iconEmoji}</span>
                <div>
                  <p className="font-display font-semibold text-white">{b.name}</p>
                  <p className="text-sm text-white/60">{b.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {state.freshCertificateId && (
        <section className="mt-6 cyber-panel flex flex-col items-start gap-3 border-emerald-400/30 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-display font-semibold text-white">🏆 You're certificate-eligible!</p>
            <p className="mt-1 text-sm text-white/60">
              You've completed the CyberTales Learning Foundations Track. Your certificate has been generated.
            </p>
          </div>
          <Link to={`/certificates/${state.freshCertificateId}`} className="cyber-btn-primary whitespace-nowrap">
            View Certificate
          </Link>
        </section>
      )}

      <section className="mt-6 max-w-md">
        {result.passed ? (
          <AskSarahButton
            variant="card"
            label="Great work! Want an advanced practice scenario?"
            episodeId={episode.id}
            resultId={result.id}
            initialPrompt="Test me"
          />
        ) : (
          <AskSarahButton
            variant="card"
            label="Let's practice the areas you missed with Sarah"
            episodeId={episode.id}
            resultId={result.id}
            initialPrompt="Help me understand my mistake"
          />
        )}
      </section>

      <section className="mt-8">
        <h2 className="font-display text-lg font-semibold text-white">Review your answers</h2>
        <div className="mt-4 space-y-4">
          {result.answers.map((answer, i) => {
            const question = questionById.get(answer.questionId)
            if (!question) return null
            const correctOptions = question.options.filter((o) => question.correctOptionIds.includes(o.id))
            const selectedOptions = question.options.filter((o) => answer.selectedOptionIds.includes(o.id))
            return (
              <div
                key={answer.questionId}
                className={`cyber-panel p-5 ${answer.isCorrect ? 'border-teal-400/20' : 'border-coral-500/20'}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="font-medium text-white">
                    {i + 1}. {question.stem}
                  </p>
                  <span className={`shrink-0 text-lg ${answer.isCorrect ? 'text-teal-400' : 'text-coral-500'}`}>
                    {answer.isCorrect ? '✓' : '✗'}
                  </span>
                </div>
                <p className="mt-3 text-sm text-white/60">
                  Your answer:{' '}
                  <span className="text-white/90">{selectedOptions.map((o) => o.text).join(', ') || '—'}</span>
                </p>
                {!answer.isCorrect && (
                  <p className="mt-1 text-sm text-white/60">
                    Correct answer: <span className="text-teal-300">{correctOptions.map((o) => o.text).join(', ')}</span>
                  </p>
                )}
                <p className="mt-3 rounded-lg bg-white/5 p-3 text-sm text-white/70">{question.explanation}</p>
              </div>
            )
          })}
        </div>
      </section>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        {!result.passed && (
          <Link to={`/episodes/${episode.id}/quiz`} className="cyber-btn-primary">
            Retake Quiz
          </Link>
        )}
        <Link to="/episodes" className="cyber-btn-secondary">
          Explore More Episodes
        </Link>
        <Link to="/dashboard" className="cyber-btn-secondary">
          Back to Dashboard
        </Link>
      </div>
    </div>
  )
}
